import { useRouter, useRouterState } from "@tanstack/react-router";
import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

import { type CrumbStack, makeCrumbStack } from "./crumbStack";
import type { BreadcrumbCrumb } from "./useSettingsBreadcrumb";

type BeforeGoBackCallback = () => boolean | void;

interface CanvasHeaderContextValue {
  canGoBack: boolean;
  canGoForward: boolean;
  goBack: () => void;
  goForward: () => void;
  grandparentStack: CrumbStack;
  invokeBeforeGoBack: () => boolean;
  pageTitleCrumb: BreadcrumbCrumb | null;
  pageTitleGrandparentCrumb: BreadcrumbCrumb | null;
  pageTitleParentCrumb: BreadcrumbCrumb | null;
  parentStack: CrumbStack;
  setBeforeGoBack: (callback: BeforeGoBackCallback | null) => void;
  titleStack: CrumbStack;
}

const CanvasHeaderContext = createContext<CanvasHeaderContextValue | null>(
  null,
);
CanvasHeaderContext.displayName = "CanvasHeaderContext";

export const CanvasHeaderProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter();

  const index = useRouterState({
    select: state => state.location.state.__TSR_index ?? 0,
  });

  const [maxIndex, setMaxIndex] = useState(index);
  const [pageTitleCrumb, setPageTitleCrumb] = useState<BreadcrumbCrumb | null>(
    null,
  );
  const [pageTitleParentCrumb, setPageTitleParentCrumb] =
    useState<BreadcrumbCrumb | null>(null);
  const [pageTitleGrandparentCrumb, setPageTitleGrandparentCrumb] =
    useState<BreadcrumbCrumb | null>(null);

  // Stacks are stable objects — mutations happen in place, no re-creation.
  const titleStack = useRef(makeCrumbStack(setPageTitleCrumb)).current;
  const parentStack = useRef(makeCrumbStack(setPageTitleParentCrumb)).current;
  const grandparentStack = useRef(
    makeCrumbStack(setPageTitleGrandparentCrumb),
  ).current;

  const beforeGoBackRef = useRef<BeforeGoBackCallback | null>(null);
  const setBeforeGoBack = useCallback(
    (callback: BeforeGoBackCallback | null) => {
      beforeGoBackRef.current = callback;
    },
    [],
  );

  useEffect(
    () =>
      router.history.subscribe(({ action }) => {
        const nextIndex = router.state.location.state.__TSR_index ?? 0;
        setMaxIndex(prev =>
          action.type === "PUSH" ? nextIndex : Math.max(prev, nextIndex),
        );
      }),
    [router],
  );

  const value = useMemo<CanvasHeaderContextValue>(
    () => ({
      canGoBack: router.history.canGoBack(),
      canGoForward: index < maxIndex,
      goBack: () => {
        const wasHandled = beforeGoBackRef.current?.();
        if (!wasHandled) router.history.back();
      },
      goForward: () => router.history.forward(),
      invokeBeforeGoBack: () => !!beforeGoBackRef.current?.(),
      pageTitleCrumb,
      pageTitleParentCrumb,
      pageTitleGrandparentCrumb,
      setBeforeGoBack,
      titleStack,
      parentStack,
      grandparentStack,
    }),
    [
      router,
      index,
      maxIndex,
      pageTitleCrumb,
      pageTitleParentCrumb,
      pageTitleGrandparentCrumb,
      setBeforeGoBack,
      titleStack,
      parentStack,
      grandparentStack,
    ],
  );

  return (
    <CanvasHeaderContext.Provider value={value}>
      {children}
    </CanvasHeaderContext.Provider>
  );
};

CanvasHeaderProvider.displayName = "CanvasHeaderProvider";

const useCanvasHeaderContext = (): CanvasHeaderContextValue => {
  const context = useContext(CanvasHeaderContext);
  if (!context) {
    throw new Error(
      "useCanvasHeaderContext must be used within a CanvasHeaderProvider",
    );
  }
  return context;
};

export const useCanvasHistoryNav = () => {
  const { canGoBack, canGoForward, goBack, goForward } =
    useCanvasHeaderContext();
  return { canGoBack, canGoForward, goBack, goForward };
};

export const useCanvasInvokeBeforeGoBack = (): (() => boolean) => {
  const { invokeBeforeGoBack } = useCanvasHeaderContext();
  return invokeBeforeGoBack;
};

export const useCanvasPageTitleCrumb = (): BreadcrumbCrumb | null =>
  useContext(CanvasHeaderContext)?.pageTitleCrumb ?? null;

export const useCanvasPageTitleParentCrumb = (): BreadcrumbCrumb | null =>
  useContext(CanvasHeaderContext)?.pageTitleParentCrumb ?? null;

export const useCanvasPageTitleGrandparentCrumb = (): BreadcrumbCrumb | null =>
  useContext(CanvasHeaderContext)?.pageTitleGrandparentCrumb ?? null;

export const useCanvasPageTitle = (
  label: string | null | undefined,
  parent?: BreadcrumbCrumb | null,
  grandparent?: BreadcrumbCrumb | null,
) => {
  const id = useId();
  const context = useContext(CanvasHeaderContext);
  const { titleStack, parentStack, grandparentStack } = context ?? {};

  useEffect(() => {
    // `undefined` means "not yet known" (e.g. data still loading) — skip.
    if (!titleStack || label === undefined) return;
    titleStack.push(id, label ? { label } : null);
    return () => titleStack.pop(id);
  }, [titleStack, id, label]);

  useEffect(() => {
    if (!parentStack || label === undefined) return;
    parentStack.push(id, parent ?? null);
    return () => parentStack.pop(id);
  }, [parentStack, id, label, parent?.label, parent?.href]);

  useEffect(() => {
    if (!grandparentStack || label === undefined) return;
    grandparentStack.push(id, grandparent ?? null);
    return () => grandparentStack.pop(id);
  }, [grandparentStack, id, label, grandparent?.label, grandparent?.href]);
};

/**
 * Registers a callback to run when the canvas header's back-nav button is
 * clicked. Return `true` to skip the default `router.history.back()`.
 */
export const useCanvasBeforeGoBack = (
  callback: BeforeGoBackCallback | undefined,
) => {
  const setBeforeGoBack = useContext(CanvasHeaderContext)?.setBeforeGoBack;

  useEffect(() => {
    if (!setBeforeGoBack) return;
    setBeforeGoBack(callback ?? null);
    return () => setBeforeGoBack(null);
  }, [setBeforeGoBack, callback]);
};
